import React, { FormEvent, useState } from 'react';
import { Handshake, Loader2 } from 'lucide-react';
import { Logo } from '../../components/Logo';
import { AuthMode, LoginViewModel } from './viewModel';

interface LoginViewProps {
  vm: LoginViewModel;
}

interface FormState {
  email: string;
  password: string;
  displayName: string;
  confirmPassword: string;
}

export function LoginView({ vm }: LoginViewProps) {
  return (
    <>
      <div className="hidden lg:block">
        <DesktopLoginView vm={vm} />
      </div>
      <div className="lg:hidden">
        <MobileLoginView vm={vm} />
      </div>
    </>
  );
}

function DesktopLoginView({ vm }: LoginViewProps) {
  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1">
        <div className="hidden flex-col items-center justify-center bg-primary p-12 text-white lg:flex lg:w-1/2">
          <Handshake className="mb-6 h-20 w-20" />
          <h1 className="mb-4 text-4xl font-bold">
            <Logo />
          </h1>
          <p className="max-w-md text-center text-xl text-white/80">
            A focused workspace for accounts, relationships, and customer operations.
          </p>
        </div>

        <div className="flex flex-1 flex-col items-center justify-center bg-gray-50 p-8">
          <div className="w-full max-w-md">
            <DesktopBrand />
            <DesktopTabs mode={vm.mode} onChange={vm.setMode} />
            <ErrorMessage error={vm.error} />
            <AuthForm
              vm={vm}
              inputClassName="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary"
              buttonClassName="flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2 font-medium text-white hover:bg-primary-hover focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <Divider backgroundClassName="bg-gray-50" />
            <GoogleButton vm={vm} />
          </div>
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}

function MobileLoginView({ vm }: LoginViewProps) {
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="border-b border-gray-100 px-5 pb-5 pt-8">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Handshake className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <Logo className="text-2xl font-bold text-gray-900" />
            <p className="mt-0.5 text-sm text-gray-500">Customer operations workspace</p>
          </div>
        </div>
      </header>

      <main className="flex-1 px-5 py-6">
        <MobileModeSwitch mode={vm.mode} onChange={vm.setMode} />
        <ErrorMessage error={vm.error} />
        <AuthForm
          vm={vm}
          inputClassName="min-h-[48px] w-full rounded-md border border-gray-300 px-4 py-3 text-base focus:border-transparent focus:ring-2 focus:ring-primary"
          buttonClassName="flex min-h-[48px] w-full items-center justify-center rounded-md bg-primary px-4 py-3 font-medium text-white hover:bg-primary-hover focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
        <Divider backgroundClassName="bg-white" />
        <GoogleButton vm={vm} mobile />
      </main>

      <LegalFooter compact />
    </div>
  );
}

function DesktopBrand() {
  return (
    <div className="mb-8 flex items-center justify-center lg:hidden">
      <Handshake className="mr-2 h-10 w-10 text-primary" />
      <Logo className="text-2xl font-bold text-gray-900" />
    </div>
  );
}

interface ModeSwitchProps {
  mode: AuthMode;
  onChange: (mode: AuthMode) => void;
}

function DesktopTabs({ mode, onChange }: ModeSwitchProps) {
  return (
    <div className="mb-6 flex border-b border-gray-200">
      <AuthTab label="Sign In" mode="signin" activeMode={mode} onChange={onChange} />
      <AuthTab label="Sign Up" mode="signup" activeMode={mode} onChange={onChange} />
    </div>
  );
}

function AuthTab({
  label,
  mode,
  activeMode,
  onChange,
}: {
  label: string;
  mode: AuthMode;
  activeMode: AuthMode;
  onChange: (mode: AuthMode) => void;
}) {
  const active = mode === activeMode;
  return (
    <button
      type="button"
      onClick={() => onChange(mode)}
      className={`flex-1 py-3 text-center font-medium transition-colors ${
        active ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {label}
    </button>
  );
}

function MobileModeSwitch({ mode, onChange }: ModeSwitchProps) {
  return (
    <div className="mb-5 grid grid-cols-2 gap-1 rounded-md bg-gray-100 p-1" role="tablist">
      {[
        { id: 'signin' as const, label: 'Sign In' },
        { id: 'signup' as const, label: 'Sign Up' },
      ].map((item) => {
        const active = item.id === mode;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className={`min-h-[44px] rounded px-3 text-sm font-medium transition-colors ${
              active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
            }`}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

function AuthForm({
  vm,
  inputClassName,
  buttonClassName,
}: {
  vm: LoginViewModel;
  inputClassName: string;
  buttonClassName: string;
}) {
  const [form, setForm] = useState<FormState>({
    email: '',
    password: '',
    displayName: '',
    confirmPassword: '',
  });
  const isSignUp = vm.mode === 'signup';

  function update(field: keyof FormState, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSignUp) {
      await vm.signUp(form.email, form.password, form.displayName, form.confirmPassword);
      return;
    }
    await vm.signIn(form.email, form.password);
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      {isSignUp && (
        <Field label="Display Name" htmlFor="signup-name">
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            value={form.displayName}
            onChange={(event) => update('displayName', event.target.value)}
            className={inputClassName}
            placeholder="Your name"
            required
          />
        </Field>
      )}
      <Field label="Email" htmlFor={isSignUp ? 'signup-email' : 'signin-email'}>
        <input
          id={isSignUp ? 'signup-email' : 'signin-email'}
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={(event) => update('email', event.target.value)}
          className={inputClassName}
          placeholder="you@example.com"
          required
        />
      </Field>
      <Field label="Password" htmlFor={isSignUp ? 'signup-password' : 'signin-password'}>
        <input
          id={isSignUp ? 'signup-password' : 'signin-password'}
          type="password"
          autoComplete={isSignUp ? 'new-password' : 'current-password'}
          value={form.password}
          onChange={(event) => update('password', event.target.value)}
          className={inputClassName}
          placeholder={isSignUp ? 'At least 6 characters' : 'Enter your password'}
          minLength={isSignUp ? 6 : undefined}
          required
        />
      </Field>
      {isSignUp && (
        <Field label="Confirm Password" htmlFor="signup-confirm-password">
          <input
            id="signup-confirm-password"
            type="password"
            autoComplete="new-password"
            value={form.confirmPassword}
            onChange={(event) => update('confirmPassword', event.target.value)}
            className={inputClassName}
            placeholder="Confirm your password"
            required
          />
        </Field>
      )}
      <SubmitButton busy={vm.busy} className={buttonClassName}>
        {isSignUp ? 'Sign Up' : 'Sign In'}
      </SubmitButton>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-gray-700">
        {label}
      </label>
      {children}
    </div>
  );
}

function SubmitButton({
  busy,
  className,
  children,
}: {
  busy: boolean;
  className: string;
  children: React.ReactNode;
}) {
  return (
    <button type="submit" disabled={busy} className={className}>
      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : children}
    </button>
  );
}

function ErrorMessage({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      {error}
    </div>
  );
}

function Divider({ backgroundClassName }: { backgroundClassName: string }) {
  return (
    <div className="relative my-6">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-gray-300" />
      </div>
      <div className="relative flex justify-center text-sm">
        <span className={`px-2 ${backgroundClassName} text-gray-500`}>Or continue with</span>
      </div>
    </div>
  );
}

function GoogleButton({ vm, mobile = false }: { vm: LoginViewModel; mobile?: boolean }) {
  return (
    <button
      type="button"
      onClick={() => vm.signInWithGoogle()}
      disabled={vm.busy}
      className={`flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        mobile ? 'min-h-[48px] px-4 py-3' : 'px-4 py-2'
      }`}
    >
      <GoogleIcon />
      Google
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function LegalFooter({ compact = false }: { compact?: boolean }) {
  return (
    <footer className={`${compact ? 'px-5 py-4' : 'py-4'} border-t border-gray-200 bg-gray-50 text-center text-xs text-gray-500`}>
      <a
        href="https://app.alignedhq.ai/terms"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:text-gray-700"
      >
        Terms
      </a>
      <span className="mx-2">·</span>
      <a
        href="https://app.alignedhq.ai/privacy"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:text-gray-700"
      >
        Privacy
      </a>
    </footer>
  );
}
